import { useTheme, Text, View, Box } from 'native-base'
import { useCallback, useMemo, useState } from 'react'
import { Animated, useWindowDimensions, StyleSheet } from 'react-native'
import {
  NavigationState,
  Route,
  SceneMap,
  SceneRendererProps,
  TabBar,
  TabBarIndicatorProps,
  TabView,
} from 'react-native-tab-view'

import { useDeviceInfo } from './useDeviceInfo'

import { radii, RFColors } from '~constants'

export type UseTabViewProps = {
  tabsData: {
    screen: () => JSX.Element
    key: string
    title: string
  }[]
}

const MARGIN_HORIZONTAL = 16

export const useTabView = ({ tabsData }: UseTabViewProps) => {
  const { colors } = useTheme()
  const { width } = useWindowDimensions()

  const { deviceSpecificWidth } = useDeviceInfo()
  const [index, setIndex] = useState(0)

  const routes = useMemo(
    () =>
      tabsData.map(({ key, title }) => {
        return { key, title }
      }),
    [tabsData]
  )

  const renderScene = SceneMap(
    tabsData.reduce((a, v) => {
      return { ...a, [v.key]: v.screen }
    }, {})
  )

  const renderLabel = useCallback(
    ({ focused, route }: { focused: boolean; color: string; route: Route }) => {
      return (
        <Text color={focused ? colors.actionBlue : colors.offMain} fontSize="sm" textAlign="center">
          {route.title}
        </Text>
      )
    },
    [colors.actionBlue, colors.offMain]
  )

  const renderIndicator = useCallback(
    ({ navigationState, position }: TabBarIndicatorProps<Route>) => {
      const itemsAmount = navigationState.routes.length
      const indicatorWidth =
        deviceSpecificWidth / itemsAmount - (MARGIN_HORIZONTAL * 2) / itemsAmount

      const indicatorPosition = position.interpolate({
        inputRange: [0, 1],
        outputRange: [0, indicatorWidth],
      })

      return (
        <Animated.View
          style={[
            styles.indicatorContainer,
            {
              transform: [{ translateX: indicatorPosition }],
              width: indicatorWidth,
            },
          ]}
        >
          <View bg="white" borderRadius="xl" h="full" />
        </Animated.View>
      )
    },
    [deviceSpecificWidth]
  )

  const renderTabBar = useCallback(
    (props: SceneRendererProps & { navigationState: NavigationState<Route> }) => {
      return (
        <Box px="4" bg="background" pb="2" roundedBottom="xl">
          <TabBar
            {...props}
            pressColor="transparent"
            renderIndicator={renderIndicator}
            renderLabel={renderLabel}
            style={styles.tabBarStyle}
          />
        </Box>
      )
    },
    [renderIndicator, renderLabel]
  )

  const tabBarView = useMemo(
    () => (
      <TabView
        initialLayout={{ width }}
        navigationState={{ index, routes }}
        onIndexChange={setIndex}
        renderScene={renderScene}
        renderTabBar={renderTabBar}
      />
    ),
    [index, width, renderScene, renderTabBar, routes]
  )

  return { tabBarView }
}

const styles = StyleSheet.create({
  indicatorContainer: { height: '100%', padding: 6 },
  tabBarStyle: {
    backgroundColor: RFColors.tabHeader,
    borderRadius: radii.xl,
  },
})
